import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faDownload } from '@fortawesome/free-solid-svg-icons';

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: Array<{ url: string; filename: string; id: string }>;
  currentIndex: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onNavigate,
}) => {
  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;

  const handlePrev = () => {
    if (onNavigate && currentIndex > 0) {
      onNavigate('prev');
    }
  };

  const handleNext = () => {
    if (onNavigate && currentIndex < images.length - 1) {
      onNavigate('next');
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = currentImage.url;
    link.download = currentImage.filename;
    link.target = '_blank';
    link.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
      handlePrev();
    } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
      handleNext();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100]"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
      >
        <FontAwesomeIcon icon={faTimes} className="text-2xl" />
      </button>

      {/* Download button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        className="absolute top-4 right-16 text-white hover:text-gray-300 transition-colors z-10"
        title="Stáhnout obrázek"
      >
        <FontAwesomeIcon icon={faDownload} className="text-2xl" />
      </button>

      {/* Previous button */}
      {hasMultiple && currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-2xl" />
        </button>
      )}

      {/* Next button */}
      {hasMultiple && currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-2xl" />
        </button>
      )}

      {/* Image */}
      <div
        className="max-w-7xl max-h-[90vh] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImage.url}
          alt={currentImage.filename}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
        
        {/* Image info */}
        <div className="text-center mt-4 text-white">
          <p className="text-sm">{currentImage.filename}</p>
          {hasMultiple && (
            <p className="text-xs text-gray-400 mt-1">
              {currentIndex + 1} / {images.length}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewerModal;

